import React, { useState } from "react";

import { useDeleteObjectDataset, useGetObjectDatasets } from "api/api-hooks/object-dataset-hooks";
import ActionButtons from "components/basic/action-buttons/ActionButtons";
import DataTable from "components/basic/data-table/DataTable";
import { ObjectDatasetDto } from "components/dashboard/object-dataset/object-dataset-types";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { Button } from "primereact/button";
import { DataTableProps } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { T } from "services/lang/LanguageService";
import ObjectDatasetForm from "./ObjectDatasetForm";

const ObjectDatasetTable: React.FC = () => {
  const [selectedObjectDataset, setSelectedDataset] = useState<ObjectDatasetDto | undefined>(undefined);
  const [openFormDialog, setOpenFormDialog] = useState(false);

  const company = useCurrentCompanyContext();
  const { data, isLoading } = useGetObjectDatasets(company?.companyReference ?? "", {
    enabled: !!company?.companyReference,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const deleteDataset = useDeleteObjectDataset();

  const renderActions = (rowData: ObjectDatasetDto) => {
    return (
      <ActionButtons
        onClickDelete={() =>
          company?.companyReference &&
          deleteDataset.mutate({
            identifier: rowData.identifier,
            companyRef: company.companyReference,
          })
        }
        onClickEdit={() => {
          setOpenFormDialog(true);
          setSelectedDataset(rowData);
        }}
      />
    );
  };

  const columns: TableColumn<ObjectDatasetDto>[] = [
    {
      field: "identifier",
      header: T("word.identifier"),
      columnKey: "identifier",
    },
    {
      field: "title",
      header: T("word.title"),
      columnKey: "title",
    },
    {
      field: "type",
      header: T("word.type"),
      style: { maxWidth: "30rem", wordBreak: "break-word" },
      columnKey: "type",
    },
    {
      header: T("word.actions"),
      columnKey: "actions",
      style: { justifyItems: "center" },
      body: renderActions,
    },
  ];

  const tableConfig: DataTableProps<ObjectDatasetDto[]> = {
    loading: isLoading,
    header: <TableHeader openDialog={() => setOpenFormDialog(true)} />,
  };

  const handleCloseDialog = () => {
    setOpenFormDialog(false);
    setSelectedDataset(undefined);
  };

  return (
    <>
      <DataTable columns={columns} data={data ?? []} tableConfig={tableConfig} />
      <Dialog
        visible={openFormDialog}
        header={T("action.create-new-dataset")}
        onHide={handleCloseDialog}
        style={{ width: "min(100%, 700px)" }}
      >
        <ObjectDatasetForm closeDialog={handleCloseDialog} objectDataset={selectedObjectDataset} />
      </Dialog>
    </>
  );
};

export default ObjectDatasetTable;

const TableHeader = ({ openDialog }: { openDialog: () => void }) => {
  return (
    <div className="flex justify-content-end flex-wrap gap-2">
      <Button
        label={T("action.create-new")}
        icon="pi pi-plus"
        size="small"
        className="component-editor-button icon-green"
        onClick={openDialog}
      />
    </div>
  );
};
